/**
 * What the four config layers actually produce for one tenant and one user.
 *
 * The counterpart to the editor, and deliberately its opposite: the editor
 * HIDES fields that are locked to global, because it lists what you may
 * change. This lists what will run, so a locked field is shown — struck
 * through at the layer that stored it — because "why does my saved value do
 * nothing?" is the question this page exists to answer.
 *
 * Resolved in the browser. See `lib/resolve-init-config.ts` for the rules and
 * why they are a second copy.
 */
import { listAccounts } from '@optum/api-client/endpoints/accounts';
import { listCapabilities } from '@optum/api-client/endpoints/capabilities';
import { getCapabilityGlobalConfig } from '@optum/api-client/endpoints/capability-global-configs';
import { getCapabilitySchema } from '@optum/api-client/endpoints/capabilities';
import { getOrgCapabilityConfig } from '@optum/api-client/endpoints/org-capability-configs';
import { getUserCapabilityConfig } from '@optum/api-client/endpoints/user-capability-configs';
import { ApiError } from '@optum/api-client/client';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { qk } from '../../lib/query-keys.js';
import { Combobox, type ComboboxOption } from '../../ui/Combobox.js';
import { DataTable } from '../../ui/DataTable.js';
import { Badge, Icon, Inline, Stack, Typography } from '../../ui/finixt.js';
import {
  CONFIG_LAYERS,
  describeValue,
  resolveInitConfig,
  type ConfigLayer,
  type LayerValue,
  type ResolvedField,
} from '../../lib/resolve-init-config.js';

const LAYER_LABEL: Record<ConfigLayer, string> = {
  default: 'Defaults',
  global: 'Global',
  organization: 'Org',
  user: 'User',
};

const SOURCE_COLOR = {
  default: 'neutral',
  global: 'warn',
  organization: 'blue',
  user: 'violet',
} as const;

/** A 404 means "nothing stored at this layer", which is a value, not an error. */
function emptyOn404(error: unknown): Record<string, unknown> {
  if (error instanceof ApiError && error.status === 404) return {};
  throw error;
}

export function ResolvedConfigTab({ organizationId }: { organizationId: string }) {
  const [capabilityId, setCapabilityId] = useState('');
  const [platformUserId, setPlatformUserId] = useState('');

  const caps = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });
  const accounts = useQuery({ queryKey: qk.accounts(), queryFn: listAccounts });

  const schema = useQuery({
    queryKey: qk.capabilitySchema(capabilityId),
    queryFn: () => getCapabilitySchema(capabilityId),
    enabled: Boolean(capabilityId),
  });
  const globalCfg = useQuery({
    queryKey: qk.capabilityGlobalConfig(capabilityId),
    queryFn: () => getCapabilityGlobalConfig(capabilityId).catch(() => null),
    enabled: Boolean(capabilityId),
  });
  const orgCfg = useQuery({
    queryKey: qk.orgCapConfig(organizationId, capabilityId),
    queryFn: () =>
      getOrgCapabilityConfig(organizationId, capabilityId)
        .then((r) => r.config)
        .catch(emptyOn404),
    enabled: Boolean(capabilityId),
  });
  const userCfg = useQuery({
    queryKey: qk.userCapConfig(organizationId, platformUserId, capabilityId),
    queryFn: () =>
      getUserCapabilityConfig(organizationId, platformUserId, capabilityId)
        .then((r) => r.config)
        .catch(emptyOn404),
    enabled: Boolean(capabilityId) && Boolean(platformUserId),
  });

  // Accounts are platform-wide; only members of this tenant can carry a
  // per-(org, user) row, so offering the rest would invite a lookup that
  // always resolves to nothing.
  // `Account.organizations` is a list of organization ids (accounts.py:265).
  const members = (accounts.data ?? []).filter((a) => a.organizations.includes(organizationId));

  const rows: ResolvedField[] =
    schema.data && capabilityId
      ? resolveInitConfig({
          fields: schema.data.init_fields,
          fieldScopes: globalCfg.data?.field_scopes ?? {},
          global: globalCfg.data?.config ?? {},
          organization: orgCfg.data ?? {},
          user: platformUserId ? (userCfg.data ?? {}) : {},
        })
      : [];

  const loading = schema.isPending || globalCfg.isPending || orgCfg.isPending;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Inline gap={11} style={{ alignItems: 'flex-end' }}>
        <Picker
          label="Capability"
          value={capabilityId}
          onChange={setCapabilityId}
          placeholder="Pick a capability"
          searchPlaceholder="Search capabilities…"
          options={(caps.data ?? []).map((c) => ({ value: c.id, label: c.name, meta: c.version }))}
        />
        <Picker
          label="User"
          value={platformUserId}
          onChange={setPlatformUserId}
          placeholder="No user — org layer only"
          searchPlaceholder="Search members…"
          options={members.map((a) => ({ value: a.platform_user_id, label: a.email }))}
        />
      </Inline>

      {!capabilityId && (
        <EmptyBlock>Pick a capability to see what its four layers resolve to.</EmptyBlock>
      )}
      {capabilityId && loading && <LoadingBlock />}
      {capabilityId && schema.isError && (
        <ErrorBlock
          message={
            schema.error instanceof Error ? schema.error.message : 'Failed to load the schema'
          }
        />
      )}
      {capabilityId && !loading && !schema.isError && rows.length === 0 && (
        <EmptyBlock>This capability has no configurable init attributes.</EmptyBlock>
      )}

      {rows.length > 0 && (
        <DataTable
          count={`${rows.length} field${rows.length === 1 ? '' : 's'}`}
          hint={
            platformUserId
              ? 'what a task from this user would run with'
              : 'pick a user to include the fourth layer'
          }
          rows={rows}
          rowKey="name"
          columns={[
            {
              key: 'name',
              label: 'Field',
              strong: true,
              render: (row) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {row.lockedToGlobal && (
                    <Icon
                      name="lock"
                      size={13}
                      // The icon is the only cue for a field nothing below
                      // global can touch, so it needs a text equivalent.
                      style={{ flexShrink: 0, color: 'var(--warn)' }}
                    />
                  )}
                  <span
                    style={{
                      fontSize: 'var(--text-meta-lg)',
                      fontWeight: 'var(--weight-semibold)',
                      color: 'var(--text-body)',
                    }}
                    className="mono"
                  >
                    {row.name}
                  </span>
                  {row.lockedToGlobal && <span className="sr-only">locked to global</span>}
                </span>
              ),
            },
            ...CONFIG_LAYERS.map((layer) => ({
              key: layer,
              label: LAYER_LABEL[layer],
              render: (row: ResolvedField) => (
                <LayerValue layer={layer} cell={row.layers[layer]} winning={row.source === layer} />
              ),
            })),
            {
              key: 'resolved',
              label: 'Resolves to',
              render: (row) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span
                    style={{
                      fontSize: 'var(--text-meta-lg)',
                      fontWeight: 'var(--weight-bold)',
                      color: 'var(--text-strong)',
                    }}
                    className="mono"
                  >
                    {row.source === 'unset' ? '—' : describeValue(row.value)}
                  </span>
                  {row.source !== 'unset' && (
                    <Badge tone={SOURCE_COLOR[row.source]}>{row.source}</Badge>
                  )}
                </span>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

function LayerValue({
  layer,
  cell,
  winning,
}: {
  layer: ConfigLayer;
  cell: LayerValue;
  winning: boolean;
}) {
  return (
    <span
      {...(cell.ignored
        ? { title: `Stored, but ${layer} cannot override this field — the merge drops it.` }
        : {})}
      className="mono"
      style={{
        fontSize: 'var(--text-meta-lg)',
        color:
          !cell.present || cell.ignored
            ? 'var(--text-faint)'
            : winning
              ? 'var(--text-accent)'
              : 'var(--text-soft)',
        textDecoration: cell.ignored ? 'line-through' : undefined,
        fontWeight: winning ? 'var(--weight-bold)' : undefined,
      }}
    >
      {describeValue(cell.value, cell.present)}
    </span>
  );
}

/** Searchable single-choice picker — the design specifies both as dropdowns. */
function Picker({
  label,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  options: ComboboxOption[];
}) {
  return (
    <Stack gap={7} style={{ width: 256 }}>
      <Typography variant="eyebrow">{label}</Typography>
      <Combobox
        aria-label={label}
        clearable
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        options={options}
      />
    </Stack>
  );
}

export function ResolvedLegend() {
  return (
    <Inline gap={11} style={{ alignItems: 'center' }}>
      <Typography variant="eyebrow">Reading this</Typography>
      <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
        Bold is the value that wins.{' '}
        <span style={{ textDecoration: 'line-through' }}>Struck through</span> is stored but
        discarded — the field is not unlocked for that layer.
      </span>
    </Inline>
  );
}
